import { useState } from "react";
import { BiChevronDown, BiChevronUp, BiSolidEditAlt } from "react-icons/bi";
import { FaRegEye } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";

const cellAlign = (a) =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";

export default function Table(props) {
  const {
    columns = [],
    data = [],
    noRecordsMessage = "No records found",
    showActions = true,
    onEdit,
    onDelete,
    checkbox,
    onView,
    renderExpansion,
    tableClassName = "",
    expandedRows: externalExpandedRows,
    onExpand,
    rowIdField = "id", // Default row identity field
  } = props;
  const [internalExpandedRows, setInternalExpandedRows] = useState({});

  const expandedRows = externalExpandedRows || internalExpandedRows;

  const toggleRow = (id) => {
    const newState = !expandedRows[id];
    if (onExpand) {
      onExpand(id, newState);
    }
    if (!externalExpandedRows) {
      setInternalExpandedRows((prev) => ({ ...prev, [id]: newState }));
    }
  };
  const cols = Array.isArray(columns) ? columns : [];
  const rows = Array.isArray(data) ? data : [];

  const idsOnPage = checkbox?.idField
    ? rows
        .map((r) => r?.[checkbox.idField])
        .filter((x) => x !== undefined && x !== null)
    : [];

  const allSelected =
    idsOnPage.length > 0 &&
    idsOnPage.every((id) => (checkbox?.selected ?? []).includes(id));

  return (
    <div className="relative bg-white">
      <table className={`w-full text-left border-collapse ${tableClassName || "text-[13px]"}`}>
        {cols.some((c) => c.w) && (
          <colgroup>
            {checkbox && <col className="w-12" />}
            {cols.map((c, i) => (
              <col key={i} className={c.w ?? "w-auto"} />
            ))}
            {showActions && <col className="w-24" />}
          </colgroup>
        )}

        <thead className="bg-gray-50 text-gray-700 uppercase text-[12px] sticky top-0 z-0 border-b border-gray-200 shadow-sm">
          <tr>
            {renderExpansion && <th className="w-10"></th>}
            {checkbox && (
              <th className="p-3 text-center align-middle">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  checked={allSelected}
                  onChange={(e) =>
                    checkbox?.onToggleAll?.(idsOnPage, e.target.checked)
                  }
                />
              </th>
            )}
            {cols.map((col, i) => (
              <th
                key={i}
                className={`p-3 font-semibold text-gray-600 uppercase tracking-tight ${cellAlign(
                  col.align,
                )}`}
              >
                {col.label}
              </th>
            ))}
            {showActions && (
              <th className="p-3 font-semibold text-gray-600 uppercase tracking-tight text-center">
                Actions
              </th>
            )}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100 text-gray-700 uppercase">
          {rows.length > 0 ? (
            rows.map((row, idx) => {
              const rowId = row[rowIdField] ?? row?.pO_No ?? row?.PO_No ?? row?.dieId ?? row?.id ?? idx;
              const isExpanded = !!expandedRows[rowId];
              return (
                <>
                  <tr
                    key={row?.id ?? row?.key ?? idx}
                    className="hover:bg-blue-50/40 transition-colors duration-200"
                  >
                    {renderExpansion && (
                      <td className="p-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleRow(rowId)}
                          className="text-blue-600 hover:bg-blue-100 rounded-full p-1"
                        >
                          {isExpanded ? (
                            <BiChevronUp size={20} />
                          ) : (
                            <BiChevronDown size={20} />
                          )}
                        </button>
                      </td>
                    )}
                    {checkbox && (
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          checked={(checkbox?.selected ?? []).includes(
                            row[checkbox.idField],
                          )}
                          onChange={(e) =>
                            checkbox?.onToggle?.(
                              row[checkbox.idField],
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                    )}

                    {cols.map((col, cidx) => {
                      const val = row[col.key];
                      const content =
                        col.type === "status" ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-sm ${
                              String(val).toLowerCase() === "done" ||
                              String(val).toLowerCase() === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {val}
                          </span>
                        ) : col.render ? (
                          col.render(val, row, idx)
                        ) : (
                          val
                        );

                      return (
                        <td
                          key={cidx}
                          className={`p-3 align-middle whitespace-nowrap ${cellAlign(
                            col.align,
                          )}`}
                        >
                          {content}
                        </td>
                      );
                    })}

                    {showActions && (
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {onEdit && (
                            <button
                              title="Edit"
                              className="p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 hover:text-blue-700 transition-all shadow-sm"
                              onClick={() => onEdit(row)}
                            >
                              <BiSolidEditAlt className="w-4 h-4" />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              title="Delete"
                              className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 hover:text-red-700 transition-all shadow-sm"
                              onClick={() => onDelete(row)}
                            >
                              <MdDeleteForever className="w-4 h-4" />
                            </button>
                          )}
                          {onView && (
                            <button
                              title="View"
                              className="p-1.5 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-200 transition-all shadow-sm"
                              onClick={() => onView(row)}
                            >
                              <FaRegEye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {isExpanded && renderExpansion && (
                    <tr>
                      <td
                        colSpan={
                          cols.length +
                          (showActions ? 1 : 0) +
                          (checkbox ? 1 : 0) +
                          1
                        }
                        className="bg-slate-50/80 p-2 border-b"
                      >
                        {renderExpansion(row)}
                      </td>
                    </tr>
                  )}
                </>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={
                  cols.length + (showActions ? 1 : 0) + (checkbox ? 1 : 0)
                }
                className="text-center py-12 text-gray-400 font-medium"
              >
                {noRecordsMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
